import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import beginPlayImg from "./img/begin.png";

const LOGIN_BG_VIDEO =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/cultural_diversity/video.mp4";
const LOGIN_BG_VIDEO_MOBILE =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/cultural_diversity/video_mob.mp4";

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const isMobilePortrait = useMobilePortrait();
  const bgVideo = isMobilePortrait ? LOGIN_BG_VIDEO_MOBILE : LOGIN_BG_VIDEO;

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;
      setBeginLoading(true);
      try {
        navigate("/intro", { replace: true });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    // Login screen must be theme-independent: no themed colors and no background images.
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    document.body.classList.remove("common-bg");
    document.body.style.backgroundImage = "none";
    document.body.style.background = "none";
    document.body.style.backgroundAttachment = "initial";
    document.body.style.backgroundPosition = "initial";
    document.body.style.backgroundSize = "initial";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    Swal.fire("You have already played!", "  ", "error").then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  return (
    <div className="login-hero">
      <video
        key={bgVideo}
        className="login-video"
        src={bgVideo}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      />

      <div className="login-overlay">
        {!isAdmin ? (
          <button
            type="button"
            className="begin-play-btn"
            disabled={beginLoading || status !== "succeeded" || !user}
            onClick={handleBeginPlay}
          >
            {beginLoading ? (
              "LOADING…"
            ) : (
              <img src={beginPlayImg} alt="Begin Play" className="begin-play-img" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default Login;
